import React, { useEffect, useMemo, useState } from 'react'
import Product from './Product'
import Hero from './Hero'
import FilterSidebar from './FilterSidebar'
import { useDispatch, useSelector } from 'react-redux'
import { getAllProducts, getAllCategories, orderBy } from '../action'
import { FiGrid, FiList } from 'react-icons/fi'

function Home() {
  const dispatch = useDispatch()
  const { productsfiltered: filteredProducts, loading, filterSidebarOpen, categories } = useSelector(state => state)
  const [viewMode, setViewMode] = useState('grid')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 12

  const sortedProducts = useMemo(() => {
    const categoryOrder = new Map((categories || []).map((category, index) => [category.id, index]))

    return [...filteredProducts].sort((a, b) => {
      const aCategoryIndex = categoryOrder.get(a.category?.id) ?? Number.MAX_SAFE_INTEGER
      const bCategoryIndex = categoryOrder.get(b.category?.id) ?? Number.MAX_SAFE_INTEGER

      if (aCategoryIndex !== bCategoryIndex) return aCategoryIndex - bCategoryIndex
      if (a.stock === 0 && b.stock > 0) return 1
      if (a.stock > 0 && b.stock === 0) return -1
      return 0
    })
  }, [filteredProducts, categories])

  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / itemsPerPage))
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedProducts = sortedProducts.slice(startIndex, startIndex + itemsPerPage)

  const pageNumbers = useMemo(() => {
    const maxButtons = 5
    const half = Math.floor(maxButtons / 2)
    let start = Math.max(1, currentPage - half)
    let end = Math.min(totalPages, start + maxButtons - 1)
    start = Math.max(1, end - maxButtons + 1)

    return Array.from({ length: end - start + 1 }, (_, index) => start + index)
  }, [currentPage, totalPages])

  useEffect(() => {
    dispatch(getAllProducts())
    dispatch(getAllCategories())
  }, [dispatch])

  useEffect(() => {
    setCurrentPage(1)
  }, [filteredProducts])

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  function handleOrderBy(e) {
    e.preventDefault()
    dispatch(orderBy(e.target.value))
  }

  if (loading) {
    return (
      <div className="vintage-loading">
        <div className="vintage-spinner"></div>
  <h2 className="mt-6 text-xl font-vintage-display font-semibold text-slate-800">Cargando productos...</h2>
  <p className="mt-2 text-slate-600">Preparando la experiencia de compra</p>
      </div>
    )
  }

  return (
    <div>
      <Hero />

  <section className="relative bg-white" id="productos">
        {/* Sidebar de filtros */}
        <FilterSidebar />

        {/* Contenido principal */}
        <div className={`transition-all duration-300 bg-white ${filterSidebarOpen ? 'lg:ml-80' : 'ml-0'}`}>
          {/* Header con controles vintage */}
          <div className="bg-white border-b border-slate-200 px-6 py-6 sticky top-20 z-30">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              {/* Info section vintage */}
              <div className="flex items-center space-x-4">
                <div className="hidden sm:block">
                  <p className="text-sm font-vintage-display text-slate-700">
                    <span className="font-semibold text-slate-800">{filteredProducts.length}</span> productos encontrados
                  </p>
                </div>
              </div>

              {/* Controls vintage */}
              <div className="flex items-center space-x-4">
                {/* View mode toggle vintage */}
                <div className="hidden md:flex items-center bg-slate-100 rounded p-1 border border-slate-200">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`vintage-filter-btn p-2 text-xs ${viewMode === 'grid' ? 'active' : ''}`}
                  >
                    <FiGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`vintage-filter-btn p-2 text-xs ${viewMode === 'list' ? 'active' : ''}`}
                  >
                    <FiList className="w-4 h-4" />
                  </button>
                </div>

                {/* Sort selector vintage */}
                <select
                  className="vintage-filter-btn py-2 px-4 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                  onChange={(e) => handleOrderBy(e)}
                >
                  <option value="">Ordenar por</option>
                  <option value="pop">Más Popular</option>
                  <option value="low">Menor Precio</option>
                  <option value="high">Mayor Precio</option>
                </select>
              </div>
            </div>
          </div>

          {/* Grid de productos vintage */}
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className={`
              ${viewMode === 'grid'
                ? 'vintage-grid'
                : 'flex flex-col space-y-6'
              }
              transition-all duration-300
            `}>
              {filteredProducts.length > 0 ? (
                paginatedProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className="animate-fadeInUp"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <Product product={product} />
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-20">
                  <div className="max-w-md mx-auto">
                    <div className="w-32 h-32 mx-auto mb-8 bg-slate-100 border-2 border-slate-200 rounded-full flex items-center justify-center">
                      <FiGrid className="w-16 h-16 text-slate-400" />
                    </div>
                    <h3 className="text-2xl font-vintage-display font-semibold text-slate-800 mb-4">No hay productos disponibles</h3>
                    <p className="text-slate-600 leading-relaxed">Ajusta los filtros o explora nuestro catálogo completo</p>
                    <button 
                      onClick={() => window.location.reload()} 
                      className="btn-vintage mt-6"
                    >
                      Explorar colección
                    </button>
                  </div>
                </div>
              )}
            </div>

            {filteredProducts.length > 0 && totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-slate-200 pt-6 mt-8">
                <p className="text-sm text-slate-600">
                  Página <span className="font-semibold text-slate-800">{currentPage}</span> de{' '}
                  <span className="font-semibold text-slate-800">{totalPages}</span>
                </p>

                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="vintage-filter-btn px-3 py-2 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>

                  {pageNumbers.map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`vintage-filter-btn px-3 py-2 text-xs ${page === currentPage ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="vintage-filter-btn px-3 py-2 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home